import React from 'react';
import styled from 'styled-components';

const PartnersSection = styled.section`
  background: var(--light-grey);
  padding: var(--spacing-3xl) 0;
  overflow: hidden;
`;

const SectionTitle = styled.h2`
  text-align: center;
  color: var(--davinci-grey);
  margin-bottom: var(--spacing-2xl);
`;

const PartnerCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: var(--spacing-md) 0;
  margin-top: var(--spacing-xl);

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--light-grey) 0%, transparent 100%);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--light-grey) 0%, transparent 100%);
  }
`;

const PartnerCarouselTrack = styled.div`
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
  width: fit-content;
  animation: partners-scroll 60s linear infinite;

  &:hover { animation-play-state: paused; }

  @keyframes partners-scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

const PartnerCard = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 12px;
  min-height: 72px;
  padding: var(--spacing-md);
  box-shadow: none;
  transition: all var(--transition-base);
  text-decoration: none;
  color: inherit;
  filter: grayscale(100%);
  isolation: isolate;
  overflow: hidden;

  &:hover {
    filter: grayscale(0%);
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 4px 24px rgba(11, 152, 206, 0.15);
    cursor: pointer;
    transform: translateY(-2px);

    img {
      transform-origin: center;
      animation: subtle-bounce 0.5s ease-out;
    }
  }

  img {
    height: 44px;
    width: auto;
    max-width: 160px;
    object-fit: contain;
    transition: all var(--transition-base);
  }

  @keyframes subtle-bounce {
    0%, 100% { transform: inherit; }
    50% { transform: inherit scale(1.05); }
  }
`;

// Partner data with scaling adjustments for visual consistency
const partnerScale: Record<string, number> = {
  'Microsoft': 1.04,
  'Google Cloud': 1.03,
  'OpenAI': 1.45,
  'AWS': 0.77,
  'NVIDIA': 1.0,
  'Supermicro': 0.66,
  'Anthropic': 0.89,
  'Pax8': 0.84,
  'Alibaba': 0.65,
};

const technologyPartners = [
  { name: 'Microsoft', logo: '/logos/partners/microsoft-logo.webp', url: 'https://azure.microsoft.com/solutions/ai', description: 'Cloud & Enterprise Solutions' },
  { name: 'Google Cloud', logo: '/logos/partners/googlecloud-logo.webp', url: 'https://cloud.google.com/ai', description: 'AI & Infrastructure' },
  { name: 'OpenAI', logo: '/logos/partners/openai-logo.webp', url: 'https://openai.com', description: 'GPT & AI Innovation' },
  { name: 'AWS', logo: '/logos/partners/aws-logo.png', url: 'https://aws.amazon.com/machine-learning/', description: 'Cloud Computing' },
  { name: 'NVIDIA', logo: '/logos/partners/nvidia-logo.webp', url: 'https://www.nvidia.com/ai/', description: 'GPU & AI Computing' },
  { name: 'Supermicro', logo: '/logos/partners/supermicro-logo.webp', url: 'https://www.supermicro.com/en/solutions/edge-ai', description: 'AI Server Hardware' },
  { name: 'Anthropic', logo: '/logos/partners/anthropic-logo.png', url: 'https://www.anthropic.com', description: 'Advanced AI Models' },
  { name: 'Pax8', logo: '/logos/partners/pax8-logo.webp', url: 'https://www.pax8.com', description: 'Cloud Distribution' },
  { name: 'Alibaba', logo: '/logos/partners/alibaba-logo.webp', url: 'https://www.alibabacloud.com/solutions/ai', description: 'Global Cloud Services' },
];

interface TechnologyPartnersProps {
  showTitle?: boolean;
  className?: string;
}

const TechnologyPartners: React.FC<TechnologyPartnersProps> = ({ showTitle = true, className }) => {
  return (
    <PartnersSection className={className}>
      <div className="container">
        {showTitle && <SectionTitle>Our Technology Partners</SectionTitle>}
        <PartnerCarouselContainer>
          <PartnerCarouselTrack>
            {[...technologyPartners, ...technologyPartners].map((partner, index) => (
              <PartnerCard
                key={`${partner.name}-${index}`}
                href={partner.url}
                title={`${partner.name} - ${partner.description}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  style={{
                    transform: `scale(${partnerScale[partner.name] ?? 1})`,
                    transformOrigin: 'center',
                    willChange: 'transform'
                  }}
                />
              </PartnerCard>
            ))}
          </PartnerCarouselTrack>
        </PartnerCarouselContainer>
      </div>
    </PartnersSection>
  );
};

export default TechnologyPartners;